import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { subDays } from "date-fns/subDays";
import { startOfDay } from "date-fns/startOfDay";
import { format } from "date-fns/format";
import { getDateFnsLocale } from "@tellescope/utils";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { Card, CardContent } from "@tellescope/ui";
import { ChevronRight } from "lucide-react";
import type { EnduserObservation } from "@tellescope/types-models";
import { buildSeries, todayValue, MACRO_COLORS, type MetricDef } from "./metrics";

interface MetricTrendCardProps {
  metric: MetricDef;
  metricLabel: string;
  weightEntries: EnduserObservation[];
  foodEntries: EnduserObservation[];
  waterEntries: EnduserObservation[];
  onClick: () => void;
}

export const MetricTrendCard = ({
  metric,
  metricLabel,
  weightEntries,
  foodEntries,
  waterEntries,
  onClick,
}: MetricTrendCardProps) => {
  const { t, i18n } = useTranslation("portal");
  const locale = getDateFnsLocale(i18n.language);

  const series30d = useMemo(() => {
    const cutoff = subDays(new Date(), 30);
    return buildSeries(metric, weightEntries, foodEntries, waterEntries, "lb").filter(
      (p) => new Date(p.date) >= cutoff
    );
  }, [metric, weightEntries, foodEntries, waterEntries]);

  const value = todayValue(metric, weightEntries, foodEntries, waterEntries, "lb") ?? 0;
  const lineColor = MACRO_COLORS[metric.id] ?? "var(--color-accent, #171717)";

  const comparison = useMemo(() => {
    const weekAgoDate = startOfDay(subDays(new Date(), 7));
    const weekAgoLabel = format(weekAgoDate, "EEEE", { locale });
    const point = series30d.find((p) => startOfDay(new Date(p.date)).getTime() === weekAgoDate.getTime());
    if (!point) return { label: t("No data"), delta: undefined };
    const delta = value - point.value;
    if (delta === 0) return { label: t("No change compared to last {{day}}", { day: weekAgoLabel }), delta: 0 };
    return {
      label: t("{{sign}}{{amount}}{{unit}} compared to last {{day}}", {
        sign: delta > 0 ? "+" : "",
        amount: delta.toFixed(metric.decimals),
        unit: metric.unit === "kcal" ? "" : metric.unit,
        day: weekAgoLabel,
      }),
      delta,
    };
  }, [series30d, value, metric, t, locale]);

  return (
    <Card className="cursor-pointer hover:bg-muted/40 transition-colors" onClick={onClick}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{metricLabel}</p>
            <p className="text-xl font-bold tabular-nums">
              {value} <span className="text-sm font-normal text-muted-foreground">{metric.unit}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center h-6 px-2 rounded-full bg-muted text-muted-foreground text-xs font-medium tabular-nums">
              {comparison.label}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
        {series30d.length >= 2 && (
          <div className="h-8">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series30d} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                <Line type="monotone" dataKey="value" stroke={lineColor} strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
